// @em/blocks — a THEME's tokens as CSS: the variables and the Mantine override that put an event site (or a preview of
// one) in the host's look. ROADMAP A2e #89.
//
// ⭐ ONE place turns tokens into CSS. It lived in events-web's `EventThemeProvider` while the event site was the only
// thing that rendered a theme; the app's theme editor now previews one too, and two copies of this mapping would drift —
// the editor would show a look the site does not draw. events-web's `EventThemeScope` / `eventThemeVars` are thin
// wrappers over these.
//
// Two layers, applied together by the caller:
//   1. `themeCssVars` — CSS variables on the themed subtree: the `--mantine-*` ones (existing Mantine-based blocks
//      re-theme with no per-block change) and the design-system `--bg / --ink / --accent / …` ones (`T.*` in tokens.ts).
//   2. `themeMantineOverride` — what Mantine models natively (font families, radius) plus per-component styles: Card and
//      Input surfaces, and (A2e #89) the BUTTON roles.
//
// ⓘ A2e #89 wired three token groups the site stored but never drew:
//   • `button.primary` { bg, text } — every FILLED button with no colour of its own (RSVP, Buy tickets, a block's Sign up);
//   • `button.secondary` { bg, text, border } — every DEFAULT-variant button with no colour of its own (My RSVP, My tickets,
//     Add to calendar, a drawer's Cancel);
//   • `element.link.color` (→ `--mantine-color-anchor`, `--link`) and `element.eyebrow.color` (→ `--eyebrow`);
//   • `element.title.family` (→ `--font-title`) — the event title's own font (owner: hosts often want one apart).
// Absent keys fall back to the accent / Mantine's defaults, exactly as before. The server checks every value
// (eventsmagic-api `boundary/theme.ts`); they are applied through React style objects and Mantine's CSS variables.
//
// ⚠ SERVER-SAFE LEAF (no hooks, no views, type-only Mantine import) — importable as `@em/blocks/themeStyle`.
import type { CSSProperties } from 'react';
import type { MantineThemeOverride } from '@mantine/core';
import type { PublicThemeTokens } from '@em/api';

import { blockTextScale } from './blockFonts';

const set = (vars: Record<string, string | undefined>): Record<string, string> => {
  const out: Record<string, string> = {};

  Object.entries(vars).forEach(([k, v]) => {
    if (v) {
      out[k] = v;
    }
  });

  return out;
};

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

/** The element-role colour `element.<role>.color`, or undefined. */
const elementColor = (tokens: PublicThemeTokens, role: string): string | undefined => {
  const el = tokens.element as Record<string, any> | undefined;

  return str(el?.[role]?.color);
};

/**
 * The site's HEADING font when the theme names none: the display font `T.fontDisplay` draws block titles and the event
 * title in — which, with no theme display font, is the body font. 2026-10-24 (owner, an unthemed event's "Before your
 * class" page): a block's title was in the body font while the headings INSIDE its text came from Mantine's
 * `--mantine-font-family-headings`, which the root provider sets to the dashboard brand's serif — two families in one
 * section, and nothing a host could change. Mantine headings (`Title`, a text block's h1–h6) now follow the same font.
 * ⓘ Read on the element that declares it: `--font-display` there (a theme's), else that element's body font.
 */
export const SITE_HEADING_FONT = 'var(--font-display, var(--mantine-font-family))';

/**
 * THE SITE'S HEADINGS — size, line height and weight of h1–h6, owned by the event site, not borrowed from the app
 * (owner, 2026-10-25: "We should not use any styling from the app - all should come from a theme and if theme has no h5
 * styling you need to add it"). A Mantine `Title` and every heading in host-written text read `--mantine-hN-*`, which the
 * APP's theme sets on :root; they are declared here, with the theme's variables, on the site's root and on every drawer.
 * The values are the scale the site has always drawn; a theme's heading FONT is `font.display`, and its heading SIZE
 * (`font.headingSize`, 2026-10-25) multiplies every one of them through `--em-heading-scale` — the same step a content
 * block's Text size uses, so the two compose (`BLOCK_HEADING_VARS`, the block title).
 * ⓘ Inside a block, headings sit under the block's title (`BLOCK_HEADING_VARS`, blockFonts.ts) — that wins there.
 */
const SITE_HEADINGS: Record<string, { size: string; lineHeight: string }> = {
  h1: { size: '2.5rem', lineHeight: '1.1' },
  h2: { size: '2rem', lineHeight: '1.15' },
  h3: { size: '1.5rem', lineHeight: '1.2' },
  h4: { size: '1.25rem', lineHeight: '1.3' },
  h5: { size: '1rem', lineHeight: '1.4' },
  h6: { size: '0.875rem', lineHeight: '1.45' },
};

export const SITE_HEADING_VARS: Record<string, string> = Object.fromEntries(
  Object.entries(SITE_HEADINGS).flatMap(([h, v]) => [
    [`--mantine-${h}-font-size`, `calc(${v.size} * var(--em-heading-scale, 1))`],
    [`--mantine-${h}-line-height`, v.lineHeight],
    [`--mantine-${h}-font-weight`, '600'],
  ]),
);

/**
 * Map tokens to CSS variables: Mantine overrides (broad re-theme) + design-system vars. For no tokens, only what the
 * site owns whatever the theme: the heading font (`SITE_HEADING_FONT`) and the heading scale (`SITE_HEADING_VARS`).
 */
export const themeCssVars = (tokens: PublicThemeTokens | null | undefined): CSSProperties => {
  if (!tokens) {
    return { '--mantine-font-family-headings': SITE_HEADING_FONT, ...SITE_HEADING_VARS } as CSSProperties;
  }

  const c = tokens.color ?? {};
  const link = elementColor(tokens, 'link') ?? c.accent;
  const primary = tokens.button?.primary ?? {};
  const secondary = tokens.button?.secondary ?? {};

  return set({
    ...SITE_HEADING_VARS,
    // The theme's heading size (2026-10-25): a step of the content blocks' text-size scale; absent / Default = none.
    '--em-heading-scale': blockTextScale(tokens.font?.headingSize) !== 1 ? String(blockTextScale(tokens.font?.headingSize)) : undefined,
    // ── Mantine variable overrides (re-theme existing Mantine-based blocks) ──
    '--mantine-color-body': c.background,
    '--mantine-color-text': c.ink,
    '--mantine-color-dimmed': c.muted,
    // Input placeholder colour — the muted theme ink, so placeholders read on the themed input surface.
    '--mantine-color-placeholder': c.muted,
    // Input labels / descriptions (@em/ui's theme reads these, falling back to its greys): the theme ink, so a field's
    // label reads on a dark event page — not only inside a drawer.
    '--em-input-label-color': c.ink,
    '--em-input-description-color': c.muted,
    '--mantine-color-default': c.surface,
    '--mantine-color-default-hover': c.surfaceAlt,
    '--mantine-color-default-border': c.line,
    // Text/icon colour for default-variant components (e.g. the "Add to calendar" /
    // "My RSVP" outline buttons). Without this it keeps the base theme's dark ink and
    // goes unreadable on a dark event theme.
    '--mantine-color-default-color': c.ink,
    '--mantine-font-family': tokens.font?.body,
    '--mantine-font-family-headings': tokens.font?.display || SITE_HEADING_FONT,
    '--mantine-radius-default': tokens.shape?.radius,
    // Primary/accent — cover both the primary-color vars and the brand shades that
    // some blocks reference directly (e.g. carousel dots use --mantine-color-brand-6).
    '--mantine-primary-color-filled': c.accent,
    '--mantine-primary-color-filled-hover': c.accent,
    '--mantine-primary-color-light': c.accentWash,
    '--mantine-primary-color-light-hover': c.accentWash,
    // The TEXT of a light-variant control (the ticket form's "Apply") — unset, it stayed the app's brand colour on the
    // theme's tint (2026-10-25).
    '--mantine-primary-color-light-color': c.accent,
    '--mantine-primary-color-contrast': c.accentInk,
    '--mantine-color-brand-5': c.accent,
    '--mantine-color-brand-6': c.accent,
    '--mantine-color-brand-7': c.accent,
    '--mantine-color-brand-filled': c.accent,
    '--mantine-color-brand-filled-hover': c.accent,
    '--mantine-color-brand-light': c.accentWash,
    // 2026-10-25 — the rest of the brand's roles a control reads (a light / outline / subtle button's text, a link-like
    // text): unset, they stayed the APP's brand colour (the ticket form's "Apply" was rust on the theme's tint).
    '--mantine-color-brand-light-hover': c.accentWash,
    '--mantine-color-brand-light-color': c.accent,
    '--mantine-color-brand-outline': c.accent,
    '--mantine-color-brand-outline-hover': c.accentWash,
    '--mantine-color-brand-text': c.accent,
    '--mantine-color-brand-8': c.accent,
    '--mantine-color-brand-9': c.accent,
    // Anchors resolve to a COMPUTED colour at theme-build time, so override it
    // directly (overriding the brand shades alone doesn't move it). A2e #89: the theme's link colour, else the accent.
    '--mantine-color-anchor': link,

    // ── Design-system vars (the block-gallery vocabulary; adopt incrementally) ──
    '--bg': c.background,
    '--surface': c.surface,
    '--surface-2': c.surfaceAlt,
    '--ink': c.ink,
    '--ink-soft': c.inkSoft,
    '--muted': c.muted,
    '--line': c.line,
    '--line-strong': c.lineStrong,
    '--accent': c.accent,
    '--accent-ink': c.accentInk,
    '--accent-wash': c.accentWash,
    '--accent-line': c.accentLine,
    '--radius': tokens.shape?.radius,
    '--font-display': tokens.font?.display,
    '--font-body': tokens.font?.body,
    // 2026-10-24 (owner: a black & white theme that greys "all images/videos on the event website") — read by every
    // picture, video and background picture (events-web globals.css, `backgroundLayerStyle`). Absent = colour.
    '--em-media-filter': tokens.effects?.media === 'grayscale' ? 'grayscale(1)' : undefined,
    // A2e #89 — the event title's own font (`element.title.family`); absent = the heading font (`T.fontTitle`).
    '--font-title': str((tokens.element as Record<string, any> | undefined)?.title?.family),
    // A2e #89 — the roles (`T.link`, `T.eyebrow`; the buttons read theirs through `themeMantineOverride`).
    '--link': link,
    '--eyebrow': elementColor(tokens, 'eyebrow'),
    '--btn-primary-bg': str(primary.bg),
    '--btn-primary-ink': str(primary.text),
    '--btn-secondary-bg': str(secondary.bg),
    '--btn-secondary-ink': str(secondary.text),
    '--btn-secondary-border': str(secondary.border),
  }) as CSSProperties;
};

/**
 * A2e #89 — inside a block with a background of ITS OWN (a custom colour, a picture) or on the theme's BLOCK colours, the
 * second button is not the theme's: its colours were chosen against the PAGE, and on a navy block a transparent button
 * with the page's dark ink is unreadable. These put it back on Mantine's default-variant colours there — which a themed
 * block has already re-pointed at tints of its own text colour (`blockThemeStyle`), exactly as before #89.
 * ⓘ The main button keeps the theme's role, like the accent does.
 */
export const BLOCK_SECONDARY_BUTTON_RESET: CSSProperties = {
  '--btn-secondary-bg': 'var(--mantine-color-default)',
  '--btn-secondary-ink': 'var(--mantine-color-default-color)',
  '--btn-secondary-border': 'var(--mantine-color-default-border)',
} as CSSProperties;

/**
 * A2e #89 — the button roles as Mantine `Button` variables. Only a button with NO colour of its own takes a role: a
 * filled one (Mantine's default variant) is primary, a `variant="default"` one is secondary. A button a block colours
 * on purpose (`color="red"`, a light/subtle variant, a gradient) keeps that.
 * ⚠ The values are READ from the `--btn-*` variables (`themeCssVars` sets them on the themed root and on every portaled
 * surface), never written in literally — so a block can re-point them (BLOCK_SECONDARY_BUTTON_RESET), and a surface
 * without them falls back to Mantine's own colours. A cleared role colour falls back the same way: the main button's
 * text to the text on the accent, the second button's to the card / text / lines colours.
 * ⓘ The hover is the role's colour mixed a little toward black / toward its text, so it still reads on every theme.
 */
const buttonVars = (tokens: PublicThemeTokens) => {
  const primary = tokens.button?.primary ?? {};
  const secondary = tokens.button?.secondary ?? {};
  const hasPrimary = !!(str(primary.bg) || str(primary.text));
  const hasSecondary = !!(str(secondary.bg) || str(secondary.text) || str(secondary.border));

  if (!hasPrimary && !hasSecondary) {
    return null;
  }

  const pBg = str(primary.bg) ? 'var(--btn-primary-bg)' : undefined;
  const sBg = 'var(--btn-secondary-bg, var(--mantine-color-default))';
  const sInk = 'var(--btn-secondary-ink, var(--mantine-color-default-color))';

  return (_theme: unknown, props: { variant?: string; color?: unknown; gradient?: unknown }) => {
    if (props.color || props.gradient) {
      return { root: {} };
    }

    if (hasPrimary && (props.variant === undefined || props.variant === 'filled')) {
      return {
        root: set({
          '--button-bg': pBg,
          '--button-hover': pBg ? `color-mix(in srgb, ${pBg} 86%, #000)` : undefined,
          '--button-color': 'var(--btn-primary-ink, var(--accent-ink, var(--mantine-primary-color-contrast)))',
        }),
      };
    }

    if (hasSecondary && props.variant === 'default') {
      return {
        root: {
          '--button-bg': sBg,
          '--button-hover': `color-mix(in srgb, ${sInk} 8%, ${sBg})`,
          '--button-color': sInk,
          '--button-bd': '1px solid var(--btn-secondary-border, var(--mantine-color-default-border))',
        },
      };
    }

    return { root: {} };
  };
};

/** Whether Mantine draws a field in its error state (an error, and error styles not switched off). */
export const inputShowsError = (props: { error?: unknown; withErrorStyles?: boolean } | null | undefined): boolean =>
  !!props?.error && props?.withErrorStyles !== false;

/** The Mantine theme override for a themed subtree: fonts, radius, and the Card / Input / Button styles. */
/**
 * The accent an Alert of `color` is drawn with: a palette name ("red") → its shade 6; any other colour as given; none →
 * blue (Mantine's default). Pure — `check-themed-drawers` runs it.
 */
export const alertAccentVar = (color: unknown): string => {
  const c = typeof color === 'string' ? color.trim() : '';

  if (!c) {
    return 'var(--mantine-color-blue-6)';
  }

  return /^[a-z]+$/.test(c) ? `var(--mantine-color-${c}-6)` : c;
};

export const themeMantineOverride = (tokens: PublicThemeTokens | null | undefined): MantineThemeOverride => {
  if (!tokens) {
    return {};
  }

  const override: MantineThemeOverride = {};

  if (tokens.font?.body) {
    override.fontFamily = tokens.font.body;
  }

  if (tokens.font?.display) {
    override.headings = { fontFamily: tokens.font.display };
  }

  if (tokens.shape?.radius) {
    override.defaultRadius = tokens.shape.radius;
  }

  const buttons = buttonVars(tokens);

  // Theme EVERY input in one place. Mantine inputs default their background to white (light color scheme)
  // regardless of the token vars, while their text follows `--mantine-color-text` (the theme ink) — so on a
  // dark event theme typed text is light-on-white = invisible. Pinning the Input styles API to the theme
  // surface + ink fixes ALL Input-based components at once (TextInput, Textarea, Select, NumberInput, …),
  // so we never have to patch inputs block-by-block again.
  override.components = {
    // ⚠ A CARD IS WHITE ON EVERY EVENT THEME UNLESS THIS IS HERE. Mantine's `Paper` takes
    // `background-color: var(--mantine-color-body)` — which the vars above re-theme correctly — but
    // `Card` OVERRIDES it with `var(--mantine-color-white)` under
    // `:where([data-mantine-color-scheme='light'])`, and `--mantine-color-white` is not a theme token
    // and never will be. So on a dark event theme the card painted white while its title kept the
    // theme's pale ink: a cream heading on a white card, i.e. invisible. Reported on the sponsorship
    // "my application" drawer; the SAME latent bug was in My tickets and the ticket purchase form.
    //
    // ⚠ `--mantine-color-white` is NOT overridden globally on purpose: 17 rules read it as a surface
    // but 10 more read it as the FOREGROUND on filled controls, so re-pointing it would put the card
    // colour inside every filled badge and button.
    //
    // `--surface` is the block gallery's card colour (see `cardStyle()` in @em/blocks), so an
    // events-web Card and a block card now agree. The `--mantine-color-body` fallback covers a
    // portaled surface that was not given the vars.
    Card: {
      styles: {
        root: {
          backgroundColor: 'var(--surface, var(--mantine-color-body))',
          color: 'var(--mantine-color-text)',
          // The app's Card border is a fixed gray-2 (a pale line on a dark theme) — the theme's line (2026-10-25).
          borderColor: 'var(--line, var(--mantine-color-default-border))',
        },
      },
    },
    Input: {
      // Use the PAGE background (not `surface`) so inputs read as editable fields (a filled `surface` grey looks
      // disabled) — while still theme-driven: text = ink, so it stays readable on light AND dark event themes. A visible
      // border delineates the field. Set `--input-bg` too: Mantine drives the field background from that custom
      // property, and a variant (e.g. filled) or higher-specificity rule otherwise beats `backgroundColor`, leaving the
      // field a stray grey block on a light drawer.
      // ⚠ The border is the theme's line EXCEPT on a field in error (2026-10-03). These styles are INLINE, so an inline
      //   `borderColor` beat Mantine's `[data-error] { --input-bd: var(--mantine-color-error) }` and no public input
      //   ever showed a red outline — the message appeared, the field did not. A function of the field's own props
      //   (Mantine passes the wrapping input's props, `error` included) leaves an errored field's border to Mantine.
      styles: (_theme: unknown, props: { error?: unknown; withErrorStyles?: boolean }) => ({
        input: {
          '--input-bg': 'var(--mantine-color-body)',
          backgroundColor: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
          ...(inputShowsError(props) ? {} : { borderColor: 'var(--mantine-color-default-border)' }),
          '--input-placeholder-color': 'var(--mantine-color-placeholder)',
        },
      }),
    },
    // ⚠ AN ALERT'S TEXT IS NEAR-BLACK ON A DARK EVENT THEME UNLESS THIS IS HERE (found 2026-10-14 on the "Report this
    // event" drawer: the summary measured rgb(34, 27, 38) on a dark wash — invisible; 25 public Alerts had no fix, the
    // Contact Us drawer's among them). Same family as Card above: Mantine colours the light variant from its own
    // light-scheme palette, not the theme's ink. Every Alert now draws the theme's ink on a wash of ITS colour mixed into
    // the page (`themedAlertStyles` in events-web did this per call; it still may, and a call's own `styles` win).
    Alert: {
      styles: (_theme: unknown, props: { color?: unknown }) => {
        const accent = alertAccentVar(props?.color);

        return {
          root: {
            backgroundColor: `color-mix(in srgb, ${accent} 14%, var(--mantine-color-body))`,
            borderColor: `color-mix(in srgb, ${accent} 45%, var(--mantine-color-body))`,
            borderWidth: 1,
            borderStyle: 'solid',
          },
          title: { color: 'var(--mantine-color-text)' },
          message: { color: 'var(--mantine-color-text)' },
          icon: { color: accent },
        };
      },
    },
    ...(buttons ? { Button: { vars: buttons } } : {}),
    // ⚠ EVERY DROPDOWN IS WHITE ON A DARK EVENT THEME UNLESS THIS IS HERE (owner, 2026-10-24: "make sure everything is
    // using themes"). Mantine paints a Popover dropdown `--mantine-color-white` on the light scheme, and the dropdown is
    // PORTALED to <body>, outside the themed subtree, so it does not even have the theme's variables. Select,
    // MultiSelect, Autocomplete, the address search, the date pickers and the session picker's jump all draw through
    // Popover (Combobox does not rename it); Menu and HoverCard rename it, so they are listed too. The override reaches a
    // portal through React context, so the dropdown carries the vars itself, then reads them: the theme's surface, ink
    // and line, and its hover tint for the option or day under the pointer (Mantine's light hover is gray-0).
    // ⓘ A call's own `styles` still win (events-web `dropdownSurfaceStyle`, written before this, says the same).
    ...Object.fromEntries(['Popover', 'Menu', 'HoverCard'].map((name) => [name, { styles: { dropdown: themedDropdown(tokens) } }])),
  };

  return override;
};

/** A portaled dropdown on the theme (see `themeMantineOverride`): its variables, then the surface, ink and line they set. */
const themedDropdown = (tokens: PublicThemeTokens): CSSProperties =>
  ({
    ...themeCssVars(tokens),
    backgroundColor: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    // Its own font too: outside the page it would inherit <body>'s — the app's.
    fontFamily: 'var(--mantine-font-family)',
    borderColor: 'var(--mantine-color-default-border)',
    '--mantine-color-gray-0': 'var(--mantine-color-default-hover)',
  }) as CSSProperties;
